import { useEffect, useRef, useState } from 'react';
import {
    useProjectFiltersQuery,
    useProjectSamplingCadenceQuery,
} from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import {
    getCurrentProjectSelectedDateRange,
    getCurrentProjectId,
    getCurrentProjectSelectedHabitatAssayType,
} from 'src/app/projects/views/project-list/state/projectListSelector';
import { setHabitatAssayType, setSelectedDateRange } from 'src/app/projects/views/project-list/state/projectListSlice';
import { useAppDispatch, useAppSelector } from 'src/store';
import { HabitatInsightsMetricType, habitatInsightsMetricTypeLabel } from 'src/shared/types';
import { HabitatAssayLozengeOption } from '../components/lozenges/HabitatAssayLozenges';
import { HabitatAssay } from '../types/graph/project';
import { SelectOption } from '../components/select/Select';
import { availableAreaColors } from '../constants/colors';
import { SamplingEvent } from '../types/graph/sampleManifest';

export type CurrentProjectFilters = {
    times: {
        firstSampleDate: string;
        lastSampleDate: string;
    };
    habitatAssays: HabitatAssay[];
    habitatAssayTypeOptions: HabitatAssayLozengeOption[];
    areaOptions: SelectOption[];
    habitatInsightsChartTypes: string[];
    habitatInsightsSites: string[];
    habitatInsightsMetricOptions: SelectOption[];
    habitatInsightsSiteOptions: SelectOption[];
};

export type SamplingEventFilterData = {
    name: string;
    fromDate: string;
    toDate: string;
    year: number;
    event: number;
};

// Stable reference so a project with no habitat assays doesn't produce a new
// array identity on every render, which would retrigger effects keyed on it.
const EMPTY_HABITAT_ASSAYS: HabitatAssay[] = [];

const useProjectFilters = () => {
    const dispatch = useAppDispatch();
    const currentProjectId = useAppSelector(getCurrentProjectId);
    const { currentData: currentProjectSamplingCadence, isFetching: isFetchingSamplingCadence } = useProjectSamplingCadenceQuery(
        {
            projectId: currentProjectId || '',
        },
        {
            skip: !currentProjectId,
        }
    );

    const areaOptionsRef = useRef<SelectOption[]>([]);
    const [habitatAssayTypeOptions, setHabitatAssayTypeOptions] = useState<HabitatAssayLozengeOption[]>([]);
    const currentProjectSelectedDateRange = useAppSelector(getCurrentProjectSelectedDateRange);
    const currentProjectSelectedHabitatAssayType = useAppSelector(getCurrentProjectSelectedHabitatAssayType);
    const habitatInsightsMetricOptionsRef = useRef<SelectOption[]>([]);
    const habitatInsightsSiteOptionsRef = useRef<SelectOption[]>([]);
    const [samplingEventFilterData, setSamplingEventFilterData] = useState<SamplingEventFilterData[]>([]);

    // Get project filters data
    const { currentData: currentFilters, isFetching: isFetchingFilters } = useProjectFiltersQuery(
        {
            projectId: currentProjectId || '',
        },
        {
            skip: !currentProjectId,
        }
    );

    useEffect(() => {
        setSamplingEventFilterData([]);
    }, [currentProjectId]);

    // Set Sampling event options (state, not a ref, so consumers re-render when cadence arrives)
    useEffect(() => {
        if (!currentProjectSamplingCadence) {
            return;
        }

        if (currentProjectSamplingCadence?.events && currentProjectSamplingCadence?.events.length > 0) {
            const yearEventsMapping: { [key: number]: number } = {};
            const newSamplingEvents: SamplingEventFilterData[] = [];

            // sort events by year and fromDate to ensure correct order in filters
            const sortedEvents = [...currentProjectSamplingCadence.events].sort((a, b) => {
                if (a.year === b.year) {
                    return a.fromDate.localeCompare(b.fromDate);
                }
                return a.year - b.year;
            });

            sortedEvents.forEach((samplingEvent: SamplingEvent) => {
                if (!yearEventsMapping[samplingEvent.year]) {
                    yearEventsMapping[samplingEvent.year] = 1;
                }
                if (samplingEvent.processed && samplingEvent.processedDate) {
                    newSamplingEvents.push({
                        name: `${samplingEvent.name}`,
                        fromDate: samplingEvent.fromDate,
                        toDate: samplingEvent.toDate,
                        year: samplingEvent.year,
                        event: yearEventsMapping[samplingEvent.year],
                    });
                }
                yearEventsMapping[samplingEvent.year]++;
            });

            const sortedByYear = newSamplingEvents.sort((a, b) => a.year - b.year);
            setSamplingEventFilterData(sortedByYear);

            // Store selected date range in the UI state based on sampling events
            if (!currentProjectSelectedDateRange) {
                dispatch(
                    setSelectedDateRange({
                        startDate: sortedByYear[0]?.fromDate || '',
                        endDate: sortedByYear[sortedByYear.length - 1]?.toDate || '',
                    })
                );
            }
        } else {
            setSamplingEventFilterData([]);
        }
    }, [currentProjectSamplingCadence]);

    useEffect(() => {
        if (!currentFilters?.filters || !currentFilters?.filters.map) {
            return;
        }
        const habitatAssays = currentFilters.filters.map.habitatAssays || [];

        const areas = currentFilters.filters.map.areas || [];

        areaOptionsRef.current = [...areas].sort().map(site => ({
            value: site,
            label: site,
        }));

        const habitatInsightsChartTypes = getCurrentProjectFilters()?.habitatInsightsChartTypes || [];

        // Get HI metric labels from shared types to ensure consistent label names across the app
        habitatInsightsMetricOptionsRef.current = [...habitatInsightsChartTypes].map(metricValue => ({
            label: habitatInsightsMetricTypeLabel[metricValue as HabitatInsightsMetricType] || metricValue.split('_').join(' '),
            value: metricValue,
        }));

        const habitatInsightsSites = getCurrentProjectFilters()?.habitatInsightsSites || [];

        habitatInsightsSiteOptionsRef.current = [...habitatInsightsSites].map(siteValue => ({
            label: siteValue.split('_').join(' '),
            value: siteValue,
        }));

        const habitatAssayTypeOptions = habitatAssays.map(entry => ({
            habitatName: entry.habitat.habitatName,
            assayName: entry.assay.assayName,
            value: entry.key,
            provider: entry.provider,
            source: entry.source,
        }));

        setHabitatAssayTypeOptions(habitatAssayTypeOptions);

        // set default habitat assay type
        if (habitatAssays.length > 0 && !currentProjectSelectedHabitatAssayType) {
            const defaultHabitatAssay = habitatAssays.find(ha => ha.provider === 'NatureMetrics') || habitatAssays[0];
            setCurrentProjectHabitatAssayType(defaultHabitatAssay.key);
        }
    }, [currentFilters]);

    const setCurrentProjectHabitatAssayType = (habitatAssayType: string) => {
        if (!currentFilters?.filters || !currentFilters.filters.map.habitatAssays) {
            return;
        }
        const habitatAssays = currentFilters.filters.map.habitatAssays || [];
        dispatch(setHabitatAssayType(habitatAssays.find(entry => entry.key === habitatAssayType)));
    };

    const getCurrentProjectFilters = (): CurrentProjectFilters | null => {
        if (!currentFilters?.filters?.map) {
            return null;
        }

        const times = currentFilters?.filters?.map.times || {
            firstSampleDate: '',
            lastSampleDate: '',
        };
        const habitatAssays = currentFilters.filters.map.habitatAssays || EMPTY_HABITAT_ASSAYS;

        return {
            times,
            habitatAssayTypeOptions,
            areaOptions: areaOptionsRef.current,
            habitatInsightsMetricOptions: habitatInsightsMetricOptionsRef.current,
            habitatInsightsSiteOptions: habitatInsightsSiteOptionsRef.current,
            habitatInsightsChartTypes: currentFilters?.filters?.map.hlcaChartTypes,
            habitatInsightsSites: currentFilters?.filters?.map.hlcaSites,
            habitatAssays,
        };
    };

    const getColourForArea = (area: string) => {
        const areaIndex = areaOptionsRef.current.findIndex(entry => entry.value === area);

        if (areaIndex === -1) {
            return '#000';
        }
        return availableAreaColors[areaIndex || 0];
    };

    return {
        currentProjectId,
        currentProjectFilters: getCurrentProjectFilters(),
        currentProjectSelectedDateRange,
        currentProjectSelectedHabitatAssayType,
        setCurrentProjectHabitatAssayType,
        isFetchingFilters,
        isFetchingSamplingCadence,
        getColourForArea,
        habitatAssayTypeOptions,
        samplingEventFilterData,
    };
};

export default useProjectFilters;
